import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { isEqual } from "lodash-es"
import { makeStyles } from "tss-react/mui"
import { useField, useFormikContext } from "formik"
import { Box, Divider, Stack, Tooltip, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { ExtraServiceDetails, ProductWithQuantity } from "../../../createOrderTypes"
import { ExtraServiceActionType } from "../../../../../../modules/merchant/admin/extraServices/extraServicesTypes"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import {
    DeliveryFulfillmentType,
    DeliveryTimesField,
    DynamicConfigKey,
    MeetingServiceTabValues,
    RemovedExtraService,
} from "../../form/formTypes"
import { toDinero } from "../../../../../../modules/currency/currencyLib"
import { useMoney } from "../../../../../../modules/currency/useMoney"
import { StandardButton } from "../../../../buttons/StandardButton"
import { useCreateOrderDialogType } from "../../../hooks/useCreateOrderDialogType"
import { useRemovedServices } from "../messages/RemovedServicesContext"
import { calculateServicePrice, isAutoAddedService, isServiceAdded, getExtraServiceBreakdown } from "./helpers"
import { ExtraServicesAccordion } from "./ExtraServicesAccordion"

const useStyles = makeStyles()((theme) => ({
    icon: {
        color: theme.palette.tailwind.slate[400],
    },
    wrap: {
        pointerEvents: "auto",
    },
    serviceButton: {
        padding: ".45rem 1.6rem !important",
        borderRadius: "0.75rem !important",
    },
    serviceAddButton: {
        backgroundColor: theme.palette.tailwind.slate[800],
        "&:hover": {
            backgroundColor: theme.palette.tailwind.slate[700],
        },
    },
    serviceInfoLeft: {
        minWidth: "9rem",
    },
    serviceItem: {
        padding: "1rem .5rem",
        "&:not(:first-of-type)": {
            borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
        },
    },
    tooltip: {
        backgroundColor: theme.palette.common.white,
        color: theme.palette.text.primary,
        padding: "1rem 1.5rem",
        boxShadow: theme.shadows[4],
        maxWidth: "15rem",
    },
    tooltipContent: {
        display: "flex",
        flexDirection: "column",
        gap: ".5rem",
    },
    tooltipRow: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        gap: "1rem",
        whiteSpace: "nowrap",
    },
    tooltipValue: {
        fontWeight: 500,
    },
}))

type ExtraServicesDeliveryAccordionProps = Readonly<{
    services: ExtraServiceDetails[]
    deliveryType: DeliveryFulfillmentType
    deliveryTime: string
    deliveryIndex: number
}>

export function ExtraServicesDeliveryAccordion({
    services,
    deliveryType,
    deliveryTime,
    deliveryIndex,
}: ExtraServicesDeliveryAccordionProps) {
    const { classes, cx } = useStyles()
    const moneyFactory = useMoney({})

    const { isRefundOrder } = useCreateOrderDialogType()
    const { addRemovedServices, clearRemovedServices } = useRemovedServices()

    const [{ value: deliveries }, , deliveriesHelpers] = useField<DeliveryTimesField>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )
    const [{ value: seatCountValue }] = useField(DynamicConfigKey.NUMBER_OF_SEATS)

    const seatCount = seatCountValue || 0

    const formikContext = useFormikContext<MeetingServiceTabValues>()
    const formikContextRef = useRef(formikContext)
    formikContextRef.current = formikContext

    const seatCountRef = useRef(seatCount)
    seatCountRef.current = seatCount

    // IMPORTANT: Formik's setValues(fn) resolves the updater against the render-snapshot values,
    // not a reducer-fresh state. So when two effects (auto-add reconciliation + seat-count price
    // update) write deliveries in the same render cycle, they both see the same snapshot. A "no-op"
    // updater that returns the snapshot unchanged would still DISPATCH that stale snapshot and
    // overwrite the other effect's concurrent change (e.g. wiping a just-auto-added delivery fee).
    // Guard: compute next here and only dispatch when it actually differs, so a no-op never clobbers.
    const setDeliveries = useCallback((updater: (current: DeliveryTimesField) => DeliveryTimesField) => {
        const current = formikContextRef.current.values[DynamicConfigKey.MULTIPLE_DELIVERIES]
        if (!current) return
        const next = updater(current)
        if (isEqual(current, next)) return
        formikContextRef.current.setValues((values) => ({
            ...values,
            [DynamicConfigKey.MULTIPLE_DELIVERIES]: next,
        }))
    }, [])

    const servicesMap = useMemo(() => {
        const map = new Map<number, ExtraServiceDetails>()
        for (const service of services) {
            map.set(service.id, service)
        }
        return map
    }, [services])

    const isAutoAdded = useCallback(
        (action: ExtraServiceDetails["action"]) => isAutoAddedService(action, deliveryType),
        [deliveryType],
    )

    const hasAutoAddedServices = useMemo(
        () => services.some((s) => s.isMandatory || isAutoAdded(s.action)),
        [services, isAutoAdded],
    )

    // Runs when services or delivery type changes.
    // - Removes services no longer returned by the API (stale).
    // - Removes auto-added services that no longer match the current delivery type.
    // - Adds services that should be auto-added for the current delivery type.
    // setDeliveries skips the dispatch when nothing changes, so a re-run with an already-correct
    // state is a no-op (no commit, no loop).
    useEffect(() => {
        if (isRefundOrder) return

        const currentIds = new Set(services.map((s) => s.id))
        const autoAddServices = services.filter((s) => {
            if (s.isMandatory) return true
            if (s.action === ExtraServiceActionType.ADD_ALWAYS) return true
            if (
                s.action === ExtraServiceActionType.ADD_WHEN_PICK_UP &&
                deliveryType === DeliveryFulfillmentType.PICK_UP
            ) {
                return true
            }
            if (
                s.action === ExtraServiceActionType.ADD_WHEN_DELIVERED &&
                deliveryType === DeliveryFulfillmentType.DELIVERY
            ) {
                return true
            }
            return false
        })

        const removedNames: RemovedExtraService[] = []

        setDeliveries((current) => {
            if (!Array.isArray(current?.deliveryTimes)) return current
            return {
                ...current,
                deliveryTimes: current.deliveryTimes.map((delivery, idx) => {
                    if (idx !== deliveryIndex) return delivery

                    let orderLines = delivery.orderLines.filter((line) => {
                        if (!line.isExtraService) return true
                        if (!currentIds.has(Number(line.id))) {
                            removedNames.push({ id: Number(line.id), name: line.name })
                            return false
                        }
                        const pw = line as ProductWithQuantity
                        if (pw.isAutoAddedExtraService) {
                            // Keep only while the service still matches the current delivery type;
                            // otherwise an auto-added delivery-only fee would never be removed when
                            // switching back to e.g. pick-up / eat-in.
                            return autoAddServices.some((s) => Number(s.id) === Number(line.id))
                        }
                        // Pre-populated services from edit mode lack isAutoAddedExtraService.
                        // Apply the same removal logic for any service that is auto-add designated.
                        const src = services.find((s) => Number(s.id) === Number(line.id))
                        if (
                            src &&
                            (src.isMandatory ||
                                src.action === ExtraServiceActionType.ADD_ALWAYS ||
                                src.action === ExtraServiceActionType.ADD_WHEN_PICK_UP ||
                                src.action === ExtraServiceActionType.ADD_WHEN_DELIVERED)
                        ) {
                            return autoAddServices.some((s) => Number(s.id) === Number(line.id))
                        }
                        return true
                    })

                    // Auto-add services that match the current delivery type
                    for (const service of autoAddServices) {
                        const isAdded = orderLines.some((l) => Number(l.id) === Number(service.id) && l.isExtraService)
                        if (!isAdded) {
                            orderLines = [
                                ...orderLines,
                                {
                                    id: service.id,
                                    name: service.name,
                                    price: calculateServicePrice(service, seatCountRef.current),
                                    isActive: service.isActive ?? true,
                                    quantity: 1,
                                    isExtraService: true,
                                    isAutoAddedExtraService: true,
                                    ...(service.isMeetingTimeRequired ? { isMeetingTimeRequired: true } : {}),
                                    ...(service.isMeetingLocationRequired ? { isMeetingLocationRequired: true } : {}),
                                    ...(service.pricingType ? { extraServicePricingType: service.pricingType } : {}),
                                    ...(service.pricePerSeat ? { extraServicePricePerSeat: service.pricePerSeat } : {}),
                                    ...(service.minimumPrice ? { extraServiceMinPrice: service.minimumPrice } : {}),
                                    ...(service.maximumPrice ? { extraServiceMaxPrice: service.maximumPrice } : {}),
                                },
                            ]
                        }
                    }

                    return { ...delivery, orderLines }
                }),
            }
        })
        if (removedNames.length > 0) {
            addRemovedServices(removedNames)
        }
        // Any service the API currently returns is available again, so revert a previous
        // "no longer available" removal (otherwise the order summary keeps it hidden forever).
        if (currentIds.size > 0) {
            clearRemovedServices(Array.from(currentIds))
        }
    }, [services, deliveryType, setDeliveries, deliveryIndex, addRemovedServices, clearRemovedServices, isRefundOrder])

    useEffect(() => {
        if (!seatCount) return

        setDeliveries((current) => {
            if (!Array.isArray(current?.deliveryTimes)) return current
            return {
                ...current,
                deliveryTimes: current.deliveryTimes.map((delivery, idx) => {
                    if (idx !== deliveryIndex) return delivery
                    return {
                        ...delivery,
                        orderLines: delivery.orderLines.map((orderLine) => {
                            const service = typeof orderLine.id === "number" && servicesMap.get(orderLine.id)
                            if (!service || !orderLine.isExtraService) return orderLine

                            const updatedPrice = calculateServicePrice(service, seatCount)
                            return {
                                ...orderLine,
                                price: updatedPrice,
                                adjustedPrice: updatedPrice,
                            }
                        }),
                    }
                }),
            }
        })
    }, [seatCount, servicesMap, setDeliveries, deliveryIndex])

    const handleAddService = (service: ExtraServiceDetails) => {
        const totalPrice = calculateServicePrice(service, seatCount)

        const newOrderLine: ProductWithQuantity = {
            id: service.id,
            name: service.name,
            price: totalPrice,
            isActive: service.isActive ?? true,
            quantity: 1,
            isExtraService: true,
            ...(service.pricingType === "PER_SEAT" && service.pricePerSeat ? { itemPrice: service.pricePerSeat } : {}),
            ...(service.isMeetingTimeRequired ? { isMeetingTimeRequired: true } : {}),
            ...(service.isMeetingLocationRequired ? { isMeetingLocationRequired: true } : {}),
            ...(service.pricingType ? { extraServicePricingType: service.pricingType } : {}),
            ...(service.pricePerSeat ? { extraServicePricePerSeat: service.pricePerSeat } : {}),
            ...(service.minimumPrice ? { extraServiceMinPrice: service.minimumPrice } : {}),
            ...(service.maximumPrice ? { extraServiceMaxPrice: service.maximumPrice } : {}),
        }

        const updatedDeliveries = deliveries.deliveryTimes.map((delivery, idx) => {
            if (idx !== deliveryIndex) return delivery
            if (isServiceAdded(delivery, service.id)) return delivery

            return {
                ...delivery,
                orderLines: [...delivery.orderLines, newOrderLine],
            }
        })

        deliveriesHelpers.setValue({
            ...deliveries,
            deliveryTimes: updatedDeliveries,
        })
    }

    const handleRemoveService = (serviceId: number) => {
        const updatedDeliveries = deliveries.deliveryTimes.map((delivery, idx) => {
            if (idx !== deliveryIndex) return delivery

            return {
                ...delivery,
                orderLines: delivery.orderLines.filter(
                    (p) => !(Number(p.id) === Number(serviceId) && p.isExtraService),
                ),
            }
        })

        deliveriesHelpers.setValue({
            ...deliveries,
            deliveryTimes: updatedDeliveries,
        })
    }

    const delivery = deliveries.deliveryTimes[deliveryIndex]

    const [initialOrderServiceIds] = useState(() => {
        if (!isRefundOrder || !delivery) return new Set<number>()
        return new Set(delivery.orderLines.filter((line) => line.isExtraService).map((line) => Number(line.id)))
    })

    if (!delivery) return null

    const displayServices = isRefundOrder
        ? services.filter((s) => initialOrderServiceIds.has(s.id))
        : services.filter((s) => !s.isMandatory && !isAutoAdded(s.action))

    if (displayServices.length === 0) return null

    return (
        <Stack gap="1rem" className={classes.wrap}>
            <LocalizedStrict
                id="create-order-extra-services-delivery"
                attrs={{ title: true }}
                vars={{ time: deliveryTime }}
            >
                <ExtraServicesAccordion title={`Delivery at ${deliveryTime}`} defaultExpanded={hasAutoAddedServices}>
                    {displayServices.map((service, serviceIndex) => {
                        const { id, name, description, pricingType, action, isMandatory } = service
                        const total = calculateServicePrice(service, seatCount)
                        const isAlreadyAdded = isServiceAdded(delivery, id)
                        const isPerSeat = pricingType === "PER_SEAT"
                        const isActionAutoAdded = !isMandatory && isAutoAdded(action)
                        const hasTooltipContent =
                            isPerSeat || !!service.minimumPrice || !!service.maximumPrice || !!description

                        const tooltipContent = hasTooltipContent ? (
                            <Stack gap="1rem">
                                <Stack className={classes.tooltipContent}>
                                    {isPerSeat && service.pricePerSeat && (
                                        <Stack className={classes.tooltipRow}>
                                            <LocalizedStrict id="create-order-extra-services-tooltip-price-per-seat-label">
                                                <Typography variant="body1">Price per seat:</Typography>
                                            </LocalizedStrict>
                                            <Typography variant="body1" className={classes.tooltipValue}>
                                                {moneyFactory.format(toDinero(service.pricePerSeat))}
                                            </Typography>
                                        </Stack>
                                    )}
                                    {service.minimumPrice && (
                                        <Stack className={classes.tooltipRow}>
                                            <LocalizedStrict id="create-order-extra-services-tooltip-min-price-label">
                                                <Typography variant="body1">Min. price:</Typography>
                                            </LocalizedStrict>
                                            <Typography variant="body1" className={classes.tooltipValue}>
                                                {moneyFactory.format(toDinero(service.minimumPrice))}
                                            </Typography>
                                        </Stack>
                                    )}
                                    {service.maximumPrice && (
                                        <Stack className={classes.tooltipRow}>
                                            <LocalizedStrict id="create-order-extra-services-tooltip-max-price-label">
                                                <Typography variant="body1">Max. price:</Typography>
                                            </LocalizedStrict>
                                            <Typography variant="body1" className={classes.tooltipValue}>
                                                {moneyFactory.format(toDinero(service.maximumPrice))}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Stack>
                                {description && (
                                    <>
                                        <Divider />
                                        <Typography variant="body1">{description}</Typography>
                                    </>
                                )}
                            </Stack>
                        ) : null

                        return (
                            <Stack key={id} className={classes.serviceItem}>
                                <Stack gap=".5rem">
                                    <Stack flexDirection="row" gap=".5rem" alignItems="center">
                                        <Typography variant="subtitle1" fontWeight={400}>
                                            {name}
                                        </Typography>
                                        {hasTooltipContent && (
                                            <Tooltip title={tooltipContent} classes={{ tooltip: classes.tooltip }}>
                                                <FontAwesomeIcon icon={faCircleInfo} className={classes.icon} />
                                            </Tooltip>
                                        )}
                                    </Stack>
                                    <Stack direction="row" alignItems="end" justifyContent="space-between" gap="1.5rem">
                                        <Stack className={classes.serviceInfoLeft}>
                                            {(() => {
                                                const bd = getExtraServiceBreakdown(
                                                    seatCount,
                                                    service.pricePerSeat,
                                                    service.minimumPrice,
                                                    service.maximumPrice,
                                                )
                                                if (bd?.kind === "seats") {
                                                    return (
                                                        <LocalizedStrict
                                                            id="create-order-extra-services-seats-breakdown"
                                                            vars={{
                                                                count: seatCount,
                                                                price: moneyFactory.format(toDinero(bd.pricePerSeat)),
                                                            }}
                                                        >
                                                            <Typography variant="body2" color="text.secondary">
                                                                {seatCount} seats x{" "}
                                                                {moneyFactory.format(toDinero(bd.pricePerSeat))}
                                                            </Typography>
                                                        </LocalizedStrict>
                                                    )
                                                }
                                                if (bd?.kind === "min") {
                                                    return (
                                                        <LocalizedStrict
                                                            id="create-order-extra-services-min-price"
                                                            vars={{ price: moneyFactory.format(toDinero(bd.minPrice)) }}
                                                        >
                                                            <Typography variant="body2" color="text.secondary">
                                                                Min. price {moneyFactory.format(toDinero(bd.minPrice))}
                                                            </Typography>
                                                        </LocalizedStrict>
                                                    )
                                                }
                                                if (bd?.kind === "max") {
                                                    return (
                                                        <LocalizedStrict
                                                            id="create-order-extra-services-max-price"
                                                            vars={{ price: moneyFactory.format(toDinero(bd.maxPrice)) }}
                                                        >
                                                            <Typography variant="body2" color="text.secondary">
                                                                Max. price {moneyFactory.format(toDinero(bd.maxPrice))}
                                                            </Typography>
                                                        </LocalizedStrict>
                                                    )
                                                }
                                                return null
                                            })()}
                                            <LocalizedStrict
                                                id="create-order-extra-services-total"
                                                vars={{ amount: moneyFactory.format(toDinero(total)) }}
                                            >
                                                <Typography variant="body2" fontWeight="bold">
                                                    Total: {moneyFactory.format(toDinero(total))}
                                                </Typography>
                                            </LocalizedStrict>
                                        </Stack>
                                        <Box flexShrink={0}>
                                            {!isRefundOrder && isMandatory ? (
                                                <LocalizedStrict
                                                    id="create-order-extra-services-mandatory-tooltip"
                                                    attrs={{ title: true }}
                                                >
                                                    <Tooltip title="">
                                                        <span>
                                                            <LocalizedStrict id="create-order-extra-services-remove-button">
                                                                <StandardButton
                                                                    fullWidth={false}
                                                                    variant="outlined"
                                                                    color="error"
                                                                    disabled
                                                                    className={classes.serviceButton}
                                                                >
                                                                    Remove
                                                                </StandardButton>
                                                            </LocalizedStrict>
                                                        </span>
                                                    </Tooltip>
                                                </LocalizedStrict>
                                            ) : !isRefundOrder && isActionAutoAdded ? (
                                                <LocalizedStrict id="create-order-extra-services-always-added-label">
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Included
                                                    </Typography>
                                                </LocalizedStrict>
                                            ) : isAlreadyAdded ? (
                                                <LocalizedStrict id="create-order-extra-services-remove-button">
                                                    <StandardButton
                                                        fullWidth={false}
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() => handleRemoveService(id)}
                                                        className={classes.serviceButton}
                                                    >
                                                        Remove
                                                    </StandardButton>
                                                </LocalizedStrict>
                                            ) : (
                                                <LocalizedStrict id="create-order-extra-services-add-button">
                                                    <StandardButton
                                                        fullWidth={false}
                                                        onClick={() => handleAddService(service)}
                                                        className={cx(classes.serviceButton, classes.serviceAddButton)}
                                                    >
                                                        Add
                                                    </StandardButton>
                                                </LocalizedStrict>
                                            )}
                                        </Box>
                                    </Stack>
                                </Stack>
                            </Stack>
                        )
                    })}
                </ExtraServicesAccordion>
            </LocalizedStrict>
        </Stack>
    )
}
